import axios from "axios";
import React, { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import { server } from "../../../server";
import { normalizeDecimalTyping } from "../../../utils/decimalInput";

const defaultSettings = {
  currency: "GBP",
  weeklyRate: 15,
  autoExpire: true,
  pricingTiers: [
    { billingCycle: "weekly", weeks: 1, price: 15, label: "1 week", currency: "GBP", isActive: true, order: 1 },
    { billingCycle: "weekly", weeks: 3, price: 45, label: "3 weeks", currency: "GBP", isActive: true, order: 2 },
    { billingCycle: "weekly", weeks: 12, price: 100, label: "12 weeks", currency: "GBP", isActive: true, order: 3 },
  ],
  discountStackable: true,
};

const formatMoney = (value) => Number(value || 0).toFixed(2);

let rowSeq = 0;
const createRowId = () => `tier-${Date.now()}-${++rowSeq}`;

const parseWeeksInput = (value) => {
  if (value === "" || value === null || value === undefined) return "";
  const n = Math.floor(Number(value));
  return Number.isFinite(n) ? n : "";
};

const toEditableTiers = (tiers = [], { sort = false } = {}) => {
  const list = (Array.isArray(tiers) ? tiers : []).map((t, i) => ({
    _rowId: t?._rowId || createRowId(),
    billingCycle: "weekly",
    weeks: parseWeeksInput(t?.weeks),
    price: t?.price === "" || t?.price === null || t?.price === undefined ? "" : t.price,
    label: String(t?.label || ""),
    currency: "GBP",
    isActive: t?.isActive !== false,
    order: Number(t?.order) || i + 1,
  }));

  if (!sort) return list;

  return [...list].sort((a, b) => {
    const aw = Number(a.weeks);
    const bw = Number(b.weeks);
    const aOk = a.weeks !== "" && Number.isFinite(aw);
    const bOk = b.weeks !== "" && Number.isFinite(bw);
    if (!aOk && !bOk) return 0;
    if (!aOk) return 1;
    if (!bOk) return -1;
    return aw - bw;
  });
};

const createBlankTier = (order) => ({
  _rowId: createRowId(),
  billingCycle: "weekly",
  weeks: "",
  price: "",
  label: "",
  currency: "GBP",
  isActive: true,
  order,
});

const finalizeDurationTiers = (tiers = []) =>
  (Array.isArray(tiers) ? tiers : [])
    .map((t, i) => {
      const weeks = Math.floor(Number(t?.weeks));
      const price = Number(t?.price);
      const weeksOk = Number.isFinite(weeks) && weeks >= 1;
      return {
        billingCycle: "weekly",
        weeks,
        price,
        label: String(t?.label || "").trim() || (weeksOk ? `${weeks} week${weeks === 1 ? "" : "s"}` : ""),
        currency: "GBP",
        isActive: true,
        order: i + 1,
      };
    })
    .sort((a, b) => a.weeks - b.weeks);

const EventSettings = ({
  entityLabel = "Event",
  entityLabelPlural = "Events",
  settingsKey = "eventSettings",
}) => {
  const [settings, setSettings] = useState({ ...defaultSettings });
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [tierToRemove, setTierToRemove] = useState(null);

  useEffect(() => {
    if (!tierToRemove) return undefined;
    const onKeyDown = (event) => {
      if (event.key === "Escape") setTierToRemove(null);
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [tierToRemove]);

  useEffect(() => {
    const loadSettings = async () => {
      try {
        setIsLoading(true);
        const { data } = await axios.get(`${server}/options/global`, {
          withCredentials: true,
        });
        const scoped = data?.options?.[settingsKey] || defaultSettings;
        const wrLoaded = Number(scoped.weeklyRate ?? defaultSettings.weeklyRate);
        const loadedTiers = Array.isArray(scoped.pricingTiers) ? scoped.pricingTiers : [];
        const merged = {
          ...defaultSettings,
          ...scoped,
          currency: "GBP",
          weeklyRate: Number.isFinite(wrLoaded) && wrLoaded > 0 ? String(wrLoaded) : String(defaultSettings.weeklyRate),
          pricingTiers: toEditableTiers(loadedTiers.length ? loadedTiers : defaultSettings.pricingTiers, {
            sort: true,
          }),
        };
        setSettings(merged);
      } catch (error) {
        toast.error(
          error?.response?.data?.message ||
            `Failed to load ${entityLabel.toLowerCase()} settings. Please try again.`
        );
      } finally {
        setIsLoading(false);
      }
    };

    loadSettings();
  }, [entityLabel, settingsKey]);

  const previewExample = useMemo(() => {
    const complete = settings.pricingTiers.filter((t) => {
      const weeks = Number(t.weeks);
      const price = Number(t.price);
      return t.weeks !== "" && Number.isFinite(weeks) && weeks >= 1 && Number.isFinite(price);
    });
    const twelve = complete.find((t) => Number(t.weeks) === 12) || complete[complete.length - 1];
    if (!twelve) return null;
    const wr = Number(settings.weeklyRate) || 0;
    const ifWeekly = wr * Number(twelve.weeks);
    const upfront = Number(twelve.price) || 0;
    const save = Math.max(0, ifWeekly - upfront);
    return { weeks: Number(twelve.weeks), wr, ifWeekly, upfront, save };
  }, [settings.pricingTiers, settings.weeklyRate]);

  const duplicateWeeks = useMemo(() => {
    const counts = {};
    settings.pricingTiers.forEach((t) => {
      const weeks = Number(t.weeks);
      if (t.weeks === "" || !Number.isFinite(weeks)) return;
      counts[weeks] = (counts[weeks] || 0) + 1;
    });
    return new Set(Object.keys(counts).filter((w) => counts[w] > 1).map(Number));
  }, [settings.pricingTiers]);

  const handleSettingChange = (field, value) => {
    setSettings((prev) => ({
      ...prev,
      [field]: value,
      currency: "GBP",
    }));
  };

  const handleWeeklyRateChange = (value) => {
    setSettings((prev) => ({
      ...prev,
      weeklyRate: normalizeDecimalTyping(value),
      currency: "GBP",
    }));
  };

  const handleTierChange = (rowId, field, value) => {
    setSettings((prev) => ({
      ...prev,
      currency: "GBP",
      pricingTiers: prev.pricingTiers.map((tier) => {
        if (tier._rowId !== rowId) return tier;
        if (field === "price") {
          return { ...tier, price: normalizeDecimalTyping(value) };
        }
        if (field === "weeks") {
          return { ...tier, weeks: parseWeeksInput(value) };
        }
        return { ...tier, [field]: value };
      }),
    }));
  };

  const addTier = () => {
    setSettings((prev) => ({
      ...prev,
      currency: "GBP",
      pricingTiers: [...prev.pricingTiers, createBlankTier(prev.pricingTiers.length + 1)],
    }));
  };

  const confirmRemoveTier = () => {
    if (!tierToRemove) return;
    setSettings((prev) => ({
      ...prev,
      currency: "GBP",
      pricingTiers: prev.pricingTiers.filter((t) => t._rowId !== tierToRemove._rowId),
    }));
    setTierToRemove(null);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!Number.isFinite(Number(settings.weeklyRate)) || Number(settings.weeklyRate) <= 0) {
      toast.error("Set the weekly payment amount (must be greater than zero).");
      return;
    }

    const draft = settings.pricingTiers;
    if (!draft.length) {
      toast.error("Add at least one campaign length.");
      return;
    }

    const incomplete = draft.find((t) => t.weeks === "" || !Number.isFinite(Number(t.weeks)) || Number(t.weeks) < 1);
    if (incomplete) {
      toast.error("Enter weeks for each campaign length (or remove unused rows).");
      return;
    }

    const weekValues = draft.map((t) => Number(t.weeks));
    if (new Set(weekValues).size !== weekValues.length) {
      toast.error("Each campaign length must use a unique number of weeks.");
      return;
    }

    const badPrice = draft.find((t) => t.price === "" || !Number.isFinite(Number(t.price)) || Number(t.price) <= 0);
    if (badPrice) {
      toast.error("Each upfront total must be greater than zero.");
      return;
    }

    const tiers = finalizeDurationTiers(draft);

    try {
      setIsSaving(true);
      const payload = {
        [settingsKey]: {
          ...settings,
          currency: "GBP",
          weeklyRate: Number(settings.weeklyRate),
          pricingTiers: tiers.map((tier, index) => ({
            billingCycle: "weekly",
            weeks: Number(tier.weeks),
            price: Number(tier.price),
            label: tier.label,
            currency: "GBP",
            order: index + 1,
            isActive: true,
          })),
        },
      };

      await axios.put(`${server}/options/global`, payload, {
        withCredentials: true,
      });

      setSettings((prev) => ({
        ...prev,
        pricingTiers: toEditableTiers(tiers, { sort: true }),
      }));
      toast.success(`${entityLabel} settings updated successfully.`);
    } catch (error) {
      toast.error(
        error?.response?.data?.message ||
          `Failed to update ${entityLabel.toLowerCase()} settings. Please try again.`
      );
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-48">
        <p className="text-sm text-slate-500">Loading {entityLabel.toLowerCase()} settings...</p>
      </div>
    );
  }

  const tiers = settings.pricingTiers;
  const wr = Number(settings.weeklyRate) || 0;
  const removeLabel =
    tierToRemove?.label?.trim() ||
    (tierToRemove?.weeks !== "" && Number.isFinite(Number(tierToRemove?.weeks))
      ? `${tierToRemove.weeks} week${Number(tierToRemove.weeks) === 1 ? "" : "s"}`
      : "this campaign length");

  return (
    <>
      <form onSubmit={handleSubmit} className="space-y-6">
        <div>
          <h2 className="text-lg font-semibold text-slate-800">{entityLabel} advert pricing</h2>
          <p className="text-sm text-slate-500 mt-1">
            Set how much sellers pay per week if they spread the cost, and set one upfront price for each campaign length.
            Shoppers always see plain English (for example &quot;Save £80 if you pay upfront&quot;).
          </p>
        </div>

        <div className="grid gap-4 md:grid-cols-2">
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={settings.autoExpire}
              onChange={(e) => handleSettingChange("autoExpire", e.target.checked)}
            />
            <span className="text-sm text-slate-700">Hide adverts automatically after the end date</span>
          </label>

          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={settings.discountStackable !== false}
              onChange={(e) => handleSettingChange("discountStackable", e.target.checked)}
            />
            <span className="text-sm text-slate-700">Allow discount codes to stack where the rest of the site allows it</span>
          </label>
        </div>

        <div className="rounded-xl border border-slate-200 overflow-hidden">
          <div className="bg-slate-50 px-4 py-3 border-b border-slate-200">
            <h3 className="text-sm font-semibold text-slate-800">Weekly payment amount</h3>
            <p className="text-xs text-slate-500 mt-0.5">
              Charged once per week if the seller chooses &quot;Pay weekly&quot; (not the full period in one go).
            </p>
          </div>
          <div className="px-4 py-4">
            <label className="block text-xs font-medium text-slate-500 mb-1">Amount per week (GBP)</label>
            <input
              type="number"
              min={0.01}
              step="0.01"
              value={settings.weeklyRate === "" ? "" : settings.weeklyRate}
              onChange={(e) => handleWeeklyRateChange(e.target.value)}
              className="w-full max-w-xs rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-[#38513b] focus:outline-none"
              required
            />
          </div>
        </div>

        <div className="rounded-xl border border-slate-200 overflow-hidden">
          <div className="bg-slate-50 px-4 py-3 border-b border-slate-200">
            <div className="flex items-center justify-between gap-4">
              <h3 className="text-sm font-semibold text-slate-800">Campaign lengths and upfront prices</h3>
              <button
                type="button"
                onClick={addTier}
                className="inline-flex shrink-0 items-center rounded-lg bg-[#38513b] px-4 py-2 text-sm font-medium text-white shadow-sm transition hover:bg-[#2f4232]"
              >
                + Add length
              </button>
            </div>
            <p className="text-xs text-slate-500 mt-1">
              For each length, enter the single payment if they pay for the whole run upfront (usually cheaper than paying every week). Rows stay in place while you edit and sort when you save.
            </p>
          </div>

          <div className="divide-y divide-slate-200">
            {tiers.map((tier) => {
              const weeksNum = Number(tier.weeks);
              const weeksOk = tier.weeks !== "" && Number.isFinite(weeksNum) && weeksNum >= 1;
              const ifWeekly = weeksOk ? wr * weeksNum : 0;
              const save = Math.max(0, ifWeekly - Number(tier.price || 0));
              const isDup = weeksOk && duplicateWeeks.has(weeksNum);
              return (
                <div
                  key={tier._rowId}
                  className="grid gap-3 px-4 py-4 md:grid-cols-[minmax(0,100px)_minmax(0,120px)_1fr_minmax(0,80px)] items-start"
                >
                  <div>
                    <label className="block text-xs font-medium text-slate-500 mb-1">Weeks</label>
                    <input
                      type="number"
                      min={1}
                      step={1}
                      value={tier.weeks}
                      onChange={(e) => handleTierChange(tier._rowId, "weeks", e.target.value)}
                      className={`w-full rounded-lg border px-2 py-2 text-sm focus:outline-none ${
                        isDup
                          ? "border-rose-400 focus:border-rose-500"
                          : "border-slate-200 focus:border-[#38513b]"
                      }`}
                    />
                    {isDup ? (
                      <p className="text-xs text-rose-600 mt-1">Weeks must be unique</p>
                    ) : null}
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-500 mb-1">Upfront total (GBP)</label>
                    <input
                      type="number"
                      min={0}
                      step="0.01"
                      value={tier.price === "" ? "" : tier.price}
                      onChange={(e) => handleTierChange(tier._rowId, "price", e.target.value)}
                      className="w-full rounded-lg border border-slate-200 px-2 py-2 text-sm focus:border-[#38513b] focus:outline-none"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-500 mb-1">Shown name</label>
                    <input
                      type="text"
                      value={tier.label || ""}
                      onChange={(e) => handleTierChange(tier._rowId, "label", e.target.value)}
                      className="w-full rounded-lg border border-slate-200 px-2 py-2 text-sm focus:border-[#38513b] focus:outline-none"
                      placeholder={weeksOk ? `${weeksNum} week${weeksNum === 1 ? "" : "s"}` : "e.g. 4 weeks"}
                    />
                    {weeksOk ? (
                      <p className="text-xs text-slate-400 mt-1">
                        vs pay weekly: GBP {formatMoney(ifWeekly)}
                        {save > 0.009 ? ` — upfront saves GBP ${formatMoney(save)}` : ""}
                      </p>
                    ) : null}
                  </div>
                  <div className="flex justify-end">
                    <button
                      type="button"
                      onClick={() => setTierToRemove(tier)}
                      className="text-sm text-rose-600 hover:underline"
                    >
                      Remove
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {previewExample && wr > 0 && (
          <div className="rounded-lg border border-emerald-100 bg-emerald-50/80 px-4 py-3 text-sm text-emerald-900">
            <p className="font-medium">Example (for your copy)</p>
            <p className="mt-1 text-emerald-800">
              {previewExample.weeks} weeks upfront: GBP {formatMoney(previewExample.upfront)}. Pay weekly: GBP{" "}
              {formatMoney(previewExample.wr)} × {previewExample.weeks} = GBP {formatMoney(previewExample.ifWeekly)}.
              {previewExample.save > 0.009 ? (
                <>
                  {" "}
                  Upfront saves GBP {formatMoney(previewExample.save)}.
                </>
              ) : (
                <> Set a lower upfront total to show a saving.</>
              )}
            </p>
          </div>
        )}

        <div className="flex justify-end">
          <button
            type="submit"
            disabled={isSaving}
            className="inline-flex items-center rounded-lg bg-[#38513b] px-6 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-[#2f4232] disabled:opacity-60"
          >
            {isSaving ? "Saving..." : "Save changes"}
          </button>
        </div>
      </form>

      {tierToRemove && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4"
          onClick={() => setTierToRemove(null)}
        >
          <div
            className="w-full max-w-sm rounded-xl bg-white p-5 shadow-xl"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="remove-tier-title"
          >
            <h2 id="remove-tier-title" className="text-base font-semibold text-slate-900">
              Remove campaign length?
            </h2>
            <p className="mt-2 text-sm text-slate-600">
              This will remove {removeLabel} from the list. Click Save changes afterwards to apply it.
            </p>
            <div className="mt-5 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setTierToRemove(null)}
                className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={confirmRemoveTier}
                className="rounded-lg bg-rose-600 px-4 py-2 text-sm font-medium text-white hover:bg-rose-700"
              >
                Remove
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default EventSettings;
